import { cn } from '@lib/utils';
import { Search, X } from 'lucide-react';
import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
} from 'react';


export interface SearchInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value' | 'type'> {
  value?: string;
  onChange?: (v: string) => void;
  onSearch?: (v: string) => void;
  debounceMs?: number;
  showShortcut?: boolean;
  containerClassName?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    onChange,
    onSearch,
    debounceMs = 300,
    showShortcut = false,
    placeholder = 'Search…',
    containerClassName,
    className,
    ...rest
  },
  ref,
) {
  const [local, setLocal] = useState(value ?? '');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (value !== undefined) setLocal(value);
  }, [value]);

  const fireSearch = useCallback(
    (v: string) => {
      if (!onSearch) return;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => onSearch(v), debounceMs);
    },
    [onSearch, debounceMs],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const set = (next: string): void => {
    setLocal(next);
    onChange?.(next);
    fireSearch(next);
  };

  return (
    <div
      className={cn(
        'group relative flex h-10 items-center rounded-lg bg-apple-gray-100 pl-3.5 pr-2 transition-all duration-200 ease-apple focus-within:bg-white focus-within:ring-3 focus-within:ring-link/20',
        containerClassName,
      )}
    >
      <Search size={16} strokeWidth={1.5} className="shrink-0 text-apple-gray-500" />
      <input
        ref={ref}
        type="search"
        value={local}
        placeholder={placeholder}
        onChange={(e) => set(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onSearch) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            onSearch(local);
          }
          rest.onKeyDown?.(e);
        }}
        className={cn(
          'ml-2 min-w-0 flex-1 bg-transparent text-[14px] text-apple-label placeholder:text-apple-placeholder outline-none',
          className,
        )}
        {...rest}
      />
      {local ? (
        <button
          type="button"
          onClick={() => set('')}
          className="ml-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-apple-gray-500 hover:bg-apple-gray-200 hover:text-apple-label-secondary transition"
          aria-label="Clear search"
        >
          <X size={14} strokeWidth={2} />
        </button>
      ) : showShortcut ? (
        <span className="ml-2 inline-flex items-center gap-0.5 rounded-md bg-white/80 px-1.5 py-0.5 text-[11px] font-medium text-apple-gray-600 ring-1 ring-apple-divider">
          ⌘K
        </span>
      ) : null}
    </div>
  );
});
